import React, { useCallback, useState } from 'react';
import { useSelected, useFocused } from 'slate-react';
import { Table, Settings, X, Plus, Trash2 } from 'lucide-react';

// Table display mode options
export const TABLE_MODES = {
  CUSTOM: 'custom',
  RISKS: 'risks',
  LOW_RISKS: 'low_risks',
  HIGH_RISKS: 'high_risks',
  CONTROLS: 'controls',
  LOW_RISK_CONTROLS: 'low_risk_controls',
  HIGH_RISK_CONTROLS: 'high_risk_controls',
  ACTIONS: 'actions',
  LOW_RISK_ACTIONS: 'low_risk_actions',
  HIGH_RISK_ACTIONS: 'high_risk_actions',
  INCIDENTS: 'incidents',
  LIKELIHOOD: 'likelihood',
  SEVERITY: 'severity',
};

export const TABLE_MODE_LABELS = {
  [TABLE_MODES.CUSTOM]: 'Custom Table',
  [TABLE_MODES.RISKS]: 'All Risks',
  [TABLE_MODES.LOW_RISKS]: 'Low Risks',
  [TABLE_MODES.HIGH_RISKS]: 'High Risks',
  [TABLE_MODES.CONTROLS]: 'All Controls',
  [TABLE_MODES.LOW_RISK_CONTROLS]: 'Low Risk Controls',
  [TABLE_MODES.HIGH_RISK_CONTROLS]: 'High Risk Controls',
  [TABLE_MODES.ACTIONS]: 'Actions',
  [TABLE_MODES.LOW_RISK_ACTIONS]: 'Low Risk Actions',
  [TABLE_MODES.HIGH_RISK_ACTIONS]: 'High Risk Actions',
  [TABLE_MODES.INCIDENTS]: 'Incidents',
  [TABLE_MODES.LIKELIHOOD]: 'Likelihood Scale',
  [TABLE_MODES.SEVERITY]: 'Severity Scale',
};

// Likelihood scale data - function that returns translated data
const getLikelihoodData = (isEnglish) => [
  { level: 'Almost certain', frequency: isEnglish ? 'More than twice a year' : 'Yılda ikiden fazla', score: 1, color: '#991433' },
  { level: 'Likely', frequency: isEnglish ? 'Once a year' : 'Yılda bir', score: 2, color: '#cc6666' },
  { level: 'Possible', frequency: isEnglish ? 'Once every five years' : 'Beş yılda bir', score: 3, color: '#fccb99' },
  { level: 'Unlikely', frequency: isEnglish ? 'Once every ten years' : 'On yılda bir', score: 4, color: '#66cccc' },
  { level: 'Rare', frequency: isEnglish ? 'At least once every ten years' : 'En az on yılda bir', score: 5, color: '#336699' },
];

// Severity scale data - function that returns translated data
const getSeverityData = (isEnglish) => [
  { 
    level: 'Insignificant', 
    operasyonel: isEnglish ? 'Operations recover within hours' : 'Operasyon saatler içinde düzelir',
    finansal: '$0 - $50,000',
    itibar: isEnglish ? 'No one knows about it' : 'Kimsenin haberi olmaz',
    stratejik: isEnglish ? 'No impact' : 'No impact',
    score: 1, 
    color: '#336699' // Blue (averse)
  },
  { 
    level: 'Minor', 
    operasyonel: isEnglish ? 'Operations recovery takes days' : 'Operasyon düzelmesi günler sürer',
    finansal: '$100,000 - $1,000,000',
    itibar: isEnglish ? 'Only company employees are aware' : 'Sadece şirket çalışanı haberdar olur',
    stratejik: isEnglish ? 'Minor impact to pursue goals' : 'Minor impact to pursue goals',
    score: 2, 
    color: '#66cccc' // Teal (minimalist)
  },
  { 
    level: 'Moderate', 
    operasyonel: isEnglish ? 'Operations recovery takes weeks' : 'Operasyon düzelmesi haftalar sürer',
    finansal: '$1,000,000 - $5,000,000',
    itibar: isEnglish ? 'Customer complaints' : 'Müşteri şikayet eder',
    stratejik: isEnglish ? 'Major impact to pursue goals' : 'Major impact to pursue goals',
    score: 3, 
    color: '#fccb99' // Peach (cautious)
  },
  { 
    level: 'Major', 
    operasyonel: isEnglish ? 'Operations recovery takes months' : 'Operasyonun düzelmesi aylar sürer',
    finansal: '$5,000,000 - $15,000,000',
    itibar: isEnglish ? 'Ridiculed on social media' : 'Sosyal medyada alay konus olunur',
    stratejik: isEnglish ? 'Significant impact to pursue goals' : 'Significant impact to pursue goals',
    score: 4, 
    color: '#cc6666' // Salmon (open)
  },
  { 
    level: 'Catastrophe', 
    operasyonel: isEnglish ? 'Operations interrupted for months' : 'Operasyon aylarca kesintiye uğrar',
    finansal: '$15,000,000+',
    itibar: isEnglish ? 'Nationwide news coverage' : 'Ülke genelinde haber konus olur',
    stratejik: isEnglish ? 'Loss of license' : 'Loss of license',
    score: 5, 
    color: '#991433' // Dark red (hungry)
  },
];

// Table header translations
const getTableLabels = (isEnglish) => ({
  // Likelihood table
  likelihood: isEnglish ? 'Likelihood' : 'Olasılık',
  frequency: isEnglish ? 'Frequency' : 'Frekans',
  score: isEnglish ? 'Score' : 'Skor',
  
  // Severity table
  impact: isEnglish ? 'Impact' : 'Etkilenme',
  operational: isEnglish ? 'Operational' : 'Operasyonel',
  financial: isEnglish ? 'Financial' : 'Finansal',
  reputational: isEnglish ? 'Reputational' : 'İtibar',
  strategic: isEnglish ? 'Strategic' : 'Stratejik',
  
  // Risk tables
  riskId: isEnglish ? 'Risk ID' : 'Risk ID',
  riskTitle: isEnglish ? 'Risk Title' : 'Risk Başlığı',
  riskOwner: isEnglish ? 'Risk Owner' : 'Risk Sahibi',
  strategicImpact: isEnglish ? 'Strategic Impact' : 'Stratejik Etki',
  riskLevel: isEnglish ? 'Risk Level' : 'Risk Seviyesi',
  probability: isEnglish ? 'Probability' : 'Olasılık',
  financialImpact: isEnglish ? 'Financial Impact' : 'Finansal Etki',
  operationalImpact: isEnglish ? 'Operational Impact' : 'Operasyonel Etki',
  reputationalImpact: isEnglish ? 'Reputational Impact' : 'İtibar Etkisi',
  financialRisk: isEnglish ? 'Financial Risk' : 'Finansal Risk',
  operationalRisk: isEnglish ? 'Operational Risk' : 'Operasyonel Risk',
  reputationalRisk: isEnglish ? 'Reputational Risk' : 'İtibar Riski',
  
  // Controls table
  controlTitle: isEnglish ? 'Control Title' : 'Kontrol Başlığı',
  controlType: isEnglish ? 'Control Type' : 'Kontrol Tipi',
  controlEfficiency: isEnglish ? 'Control Efficiency' : 'Kontrol Verimliliği',
  finding: isEnglish ? 'Finding' : 'Bulgu',
  recommendation: isEnglish ? 'Recommendation' : 'Öneri',
  noFindingDetected: isEnglish ? 'No finding detected.' : 'Herhangi bir bulgu tespit edilmemiştir.',
  noRecommendationSpecified: isEnglish ? 'No recommendation specified.' : 'Herhangi bir öneri belirtilmemiştir.',
  
  // Actions table
  actionTitle: isEnglish ? 'Action Title' : 'Aksiyon Başlığı',
  startDate: isEnglish ? 'Start Date' : 'Başlangıç',
  endDate: isEnglish ? 'End Date' : 'Bitiş',
  status: isEnglish ? 'Status' : 'Durum',
});

function DataTableElement({ attributes, children, element, reportData, onUpdate, onDelete, language = 'tr' }) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);
  const isEnglish = language === 'en';

  const mode = element.mode || TABLE_MODES.CUSTOM;
  const tableData = element.tableData || {
    headers: ['Column 1', 'Column 2', 'Column 3'],
    rows: [['', '', '']],
  };

  //console.log("Tab le report data ", reportData)
  //console.log("TABLE ELEMENT:", element);
  //console.log("TABLE MODE:", mode);
  //console.log("REPORT DATA RISKS:", reportData?.risks);
  //console.log("REPORT DATA RISKS LENGTH:", reportData?.risks?.length);
  //console.log("REPORT DATA FULL:", JSON.parse(JSON.stringify(reportData || {})));

  // Badge color helper
  const getBadgeColor = (value) => {
    if (value <= 2) return 'bg-green-500';
    if (value <= 6) return 'bg-yellow-500';
    if (value <= 12) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Filter risks by level
  const filterRisks = (risks, isLow) => {
    if (!risks) return [];
    return risks.filter(risk => {
      const isLowRisk = (
        risk.financial?.residual <= 6 && 
        risk.operational?.residual <= 6 && 
        risk.reputational?.residual <= 6
      );
      return isLow ? isLowRisk : !isLowRisk;
    });
  };

  // Filter controls by risk level
  const filterControls = (controls, isLow) => {
    if (!controls) return [];
    return controls.filter(risk => {
      const isLowRisk = (
        risk.residualScores?.financial <= 6 && 
        risk.residualScores?.operational <= 6 && 
        risk.residualScores?.reputational <= 6
      );
      return isLow ? isLowRisk : !isLowRisk;
    });
  };

  // Filter actions by associated risk level
  const filterActions = (actions, risks, isLow) => {
    if (!actions || !risks) return [];
    
    // Get low/high risk IDs
    const filteredRiskIds = filterRisks(risks, isLow).map(r => r.ID);
    
    // Filter actions that belong to filtered risks
    return actions.filter(action => {
      return filteredRiskIds.includes(action.RiskID || action.risk_id);
    });
  };

  const handleModeChange = useCallback((newMode) => {
    if (onUpdate) {
      onUpdate(element.id, { mode: newMode });
    }
  }, [element.id, onUpdate]);

  const handleTitleChange = useCallback((title) => {
    if (onUpdate) {
      onUpdate(element.id, { title });
    }
  }, [element.id, onUpdate]);

  const handleCellChange = useCallback((rowIndex, colIndex, value) => {
    const newRows = [...tableData.rows];
    newRows[rowIndex][colIndex] = value;
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { ...tableData, rows: newRows } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  const handleHeaderChange = useCallback((colIndex, value) => {
    const newHeaders = [...tableData.headers];
    newHeaders[colIndex] = value;
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { ...tableData, headers: newHeaders } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  const addColumn = useCallback(() => {
    const newHeaders = [...tableData.headers, `Column ${tableData.headers.length + 1}`];
    const newRows = tableData.rows.map(row => [...row, '']);
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { headers: newHeaders, rows: newRows } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  const removeColumn = useCallback((colIndex) => {
    if (tableData.headers.length <= 1) return;
    const newHeaders = tableData.headers.filter((_, i) => i !== colIndex);
    const newRows = tableData.rows.map(row => row.filter((_, i) => i !== colIndex));
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { headers: newHeaders, rows: newRows } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  const addRow = useCallback(() => {
    const newRow = tableData.headers.map(() => '');
    const newRows = [...tableData.rows, newRow];
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { ...tableData, rows: newRows } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  const removeRow = useCallback((rowIndex) => {
    if (tableData.rows.length <= 1) return;
    const newRows = tableData.rows.filter((_, i) => i !== rowIndex);
    if (onUpdate) {
      onUpdate(element.id, { 
        tableData: { ...tableData, rows: newRows } 
      });
    }
  }, [element.id, tableData, onUpdate]);

  // Get translated labels
  const labels = getTableLabels(isEnglish);
  const likelihoodData = getLikelihoodData(isEnglish);
  const severityData = getSeverityData(isEnglish);

  const getRiskLevel = (risk) => {
    if (!risk) return "-";

    const isLowRisk =
      (risk.financial?.residual ?? 999) <= 6 &&
      (risk.operational?.residual ?? 999) <= 6 &&
      (risk.reputational?.residual ?? 999) <= 6;

    return isLowRisk ? "Low" : "High";
  };

  // Render likelihood scale table
  const renderLikelihoodTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse border border-gray-200 text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-800 font-semibold">
              <th className="border border-gray-200 px-4 py-3 text-left">{labels.likelihood}</th>
              <th className="border border-gray-200 px-4 py-3 text-left">{labels.frequency}</th>
              <th className="border border-gray-200 px-4 py-3 text-left">{labels.score}</th>
            </tr>
          </thead>
          <tbody>
            {likelihoodData.map((item, idx) => (
              <tr key={idx} className="bg-white hover:bg-gray-50">
                <td className="border border-gray-200 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>{item.level}</span>
                  </div>
                </td>
                <td className="border border-gray-200 px-4 py-3">{item.frequency}</td>
                <td className="border border-gray-200 px-4 py-3">{item.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render severity scale table
  const renderSeverityTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-800 font-semibold">
              <th className="border border-gray-300 px-3 py-2 text-left">{labels.impact}</th>
              <th className="border border-gray-300 px-3 py-2 text-left">{labels.operational}</th>
              <th className="border border-gray-300 px-3 py-2 text-left">{labels.financial}</th>
              <th className="border border-gray-300 px-3 py-2 text-left">{labels.reputational}</th>
              <th className="border border-gray-300 px-3 py-2 text-left">{labels.strategic}</th>
              <th className="border border-gray-300 px-3 py-2 text-center">{labels.score}</th>
            </tr>
          </thead>
          <tbody>
            {severityData.map((item, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="border border-gray-300 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <span>{item.level}</span>
                  </div>
                </td>
                <td className="border border-gray-300 px-3 py-2">{item.operasyonel}</td>
                <td className="border border-gray-300 px-3 py-2">{item.finansal}</td>
                <td className="border border-gray-300 px-3 py-2">{item.itibar}</td>
                <td className="border border-gray-300 px-3 py-2">{item.stratejik}</td>
                <td className="border border-gray-300 px-3 py-2 text-center">{item.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render data table from report data
  const renderDataTable = () => {
    if (!reportData) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Table size={48} className="mx-auto mb-2 text-gray-300" />
          <p>No data available. Data will populate when report data is loaded.</p>
        </div>
      );
    }

    let data = [];
    let tableType = '';
    //console.log("reportData ", reportData)
    switch (mode) {
      case TABLE_MODES.RISKS:
        data = reportData.risks || [];
        tableType = 'risks';
        break;
      case TABLE_MODES.LOW_RISKS:
        data = filterRisks(reportData.risks, true);
        tableType = 'risks';
        break;
      case TABLE_MODES.HIGH_RISKS:
        data = filterRisks(reportData.risks, false);
        tableType = 'risks';
        break;
      case TABLE_MODES.CONTROLS:
        data = reportData.controls || [];
        tableType = 'controls';
        break;
      case TABLE_MODES.LOW_RISK_CONTROLS:
        data = filterControls(reportData.controls, true);
        tableType = 'controls';
        break;
      case TABLE_MODES.HIGH_RISK_CONTROLS:
        data = filterControls(reportData.controls, false);
        tableType = 'controls';
        break;
      case TABLE_MODES.ACTIONS:
        data = reportData.actions || [];
        tableType = 'actions';
        break;
      case TABLE_MODES.LOW_RISK_ACTIONS:
        data = filterActions(reportData.actions, reportData.risks, true);
        tableType = 'actions';
        break;
      case TABLE_MODES.HIGH_RISK_ACTIONS:
        data = filterActions(reportData.actions, reportData.risks, false);
        tableType = 'actions';
        break;
      case TABLE_MODES.INCIDENTS:
        data = reportData.incidents || [];
        tableType = 'incidents';
        break;
      case TABLE_MODES.LIKELIHOOD:
        return renderLikelihoodTable();
      case TABLE_MODES.SEVERITY:
        return renderSeverityTable();
      default:
        return null;
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Table size={32} className="mx-auto mb-2 text-gray-300" />
          <p>No {tableType} data available</p>
        </div>
      );
    }

    // Render based on table type
    if (tableType === 'risks') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-800 font-semibold">
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskId}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskTitle}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskOwner}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.strategicImpact}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskLevel}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.probability}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.financialImpact}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.operationalImpact}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.reputationalImpact}</th>
                <th className="border border-gray-300 px-2 py-2 text-center">{labels.financialRisk}</th>
                <th className="border border-gray-300 px-2 py-2 text-center">{labels.operationalRisk}</th>
                <th className="border border-gray-300 px-2 py-2 text-center">{labels.reputationalRisk}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((risk, idx) => (
                <tr key={risk.ID || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-2 py-2">{risk.ID}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.Title}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.OwnerFirstName} {risk.OwnerLastName}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.strategic_impact || risk.StrategicImpact || '-'}</td>
                  <td className="border border-gray-300 px-2 py-2"> {getRiskLevel(risk)}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.probability_inherent || risk.Probability || '-'}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.financial_inherent || risk.FinancialImpact || '-'}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.operational_inherent || risk.OperationalImpact || '-'}</td>
                  <td className="border border-gray-300 px-2 py-2">{risk.reputational_inherent || risk.ReputationalImpact || '-'}</td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-white text-xs ${getBadgeColor(risk.financial?.residual)}`}>
                      {risk.financial?.residual || '-'}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-white text-xs ${getBadgeColor(risk.operational?.residual)}`}>
                      {risk.operational?.residual || '-'}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-white text-xs ${getBadgeColor(risk.reputational?.residual)}`}>
                      {risk.reputational?.residual || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (tableType === 'controls') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-800 font-semibold">
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskId}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskTitle}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.controlTitle}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.controlType}</th>
                <th className="border border-gray-300 px-2 py-2 text-center">{labels.controlEfficiency}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.finding}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.recommendation}</th>
              </tr>
            </thead>
            <tbody>
              {data.flatMap((risk, riskIdx) => 
                (risk.controls || []).map((ctrl, ctrlIdx) => (
                  <tr key={`${riskIdx}-${ctrlIdx}`} className={riskIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-2 py-2">{risk.riskId}</td>
                    <td className="border border-gray-300 px-2 py-2">{risk.title}</td>
                    <td className="border border-gray-300 px-2 py-2">{ctrl.controlDetails || ctrl.controlTitle || ctrl.title || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2">{ctrl.controlType || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-2 text-center">
                      {ctrl.efficiencyLevel > 50 ? (
                        <i className="fas fa-thumbs-up text-gray-600"></i>
                      ) : (
                        <i className="fas fa-frown text-gray-600"></i>
                      )}
                    </td>
                    <td className="border border-gray-300 px-2 py-2">{ctrl.finding || ctrl.bulgu || labels.noFindingDetected}</td>
                    <td className="border border-gray-300 px-2 py-2">{ctrl.recommendation || ctrl.oneri || labels.noRecommendationSpecified}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (tableType === 'actions') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-800 font-semibold">
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskId}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.riskTitle}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.actionTitle}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.startDate}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.endDate}</th>
                <th className="border border-gray-300 px-2 py-2 text-left">{labels.status}</th>
              </tr>
            </thead>
            <tbody>
              {data.flatMap((risk, riskIdx) => 
                (risk.actions || []).map((action, actIdx) => (
                  <tr key={`${riskIdx}-${actIdx}`} className={riskIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-2 py-2">{risk.riskId}</td>
                    <td className="border border-gray-300 px-2 py-2">{risk.riskTitle}</td>
                    <td className="border border-gray-300 px-2 py-2">{action.title}</td>
                    <td className="border border-gray-300 px-2 py-2">{action.startDate}</td>
                    <td className="border border-gray-300 px-2 py-2">{action.dueDate}</td>
                    <td className="border border-gray-300 px-2 py-2">
                      <span className={`inline-block px-2 py-1 rounded text-white text-xs ${
                        action.status === 'Completed' ? 'bg-green-500' :
                        action.status === 'In Progress' ? 'bg-blue-500' : 'bg-gray-500'
                      }`}>
                        {action.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (tableType === 'incidents') {
      return (
        <div className="space-y-4">
          {data.map((incident, idx) => (
            <div key={idx} className="border border-gray-300 rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <tbody>
                  <tr className="bg-red-200">
                    <td className="border border-gray-300 px-4 py-2 font-bold w-1/4">Incident ID</td>
                    <td className="border border-gray-300 px-4 py-2" colSpan="2">{incident.ID}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-bold">Title</td>
                    <td className="border border-gray-300 px-4 py-2" colSpan="2">{incident.title}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-bold">Description</td>
                    <td className="border border-gray-300 px-4 py-2" colSpan="2">{incident.description}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-bold">Severity</td>
                    <td className="border border-gray-300 px-4 py-2" colSpan="2">{incident.severity}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-bold">Corrective Action</td>
                    <td className="border border-gray-300 px-4 py-2" colSpan="2">{incident.corrective_action}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Render custom editable table
  const renderCustomTable = () => (
    <div className="custom-table-container overflow-x-auto">
      <table className="min-w-full table-auto border-collapse border border-gray-300">
        <thead>
          <tr className="bg-red-200">
            {tableData.headers.map((header, colIndex) => (
              <th 
                key={colIndex}
                className="border border-gray-300 px-4 py-2 relative group"
              >
                {selected ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={header}
                      onChange={(e) => handleHeaderChange(colIndex, e.target.value)}
                      className="w-full bg-transparent border-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                    />
                    <button
                      onClick={() => removeColumn(colIndex)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"
                      title="Remove column"
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                ) : (
                  header
                )}
              </th>
            ))}
            {selected && (
              <th className="border border-gray-300 px-2 py-2 bg-gray-100">
                <button
                  onClick={addColumn}
                  className="p-1 hover:bg-blue-100 rounded"
                  title="Add column"
                >
                  <Plus size={16} className="text-blue-500" />
                </button>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tableData.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, colIndex) => (
                <td key={colIndex} className="border border-gray-300 px-4 py-2">
                  {selected ? (
                    <input
                      type="text"
                      value={cell}
                      onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                      className="w-full bg-transparent border-none focus:ring-1 focus:ring-blue-500 rounded px-1"
                    />
                  ) : (
                    cell
                  )}
                </td>
              ))}
              {selected && (
                <td className="border border-gray-300 px-2 py-2 bg-gray-100">
                  <button
                    onClick={() => removeRow(rowIndex)}
                    className="p-1 hover:bg-red-100 rounded"
                    title="Remove row"
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {selected && (
        <button
          onClick={addRow}
          className="mt-2 flex items-center gap-2 px-3 py-1 text-sm text-blue-500 hover:bg-blue-50 rounded"
        >
          <Plus size={16} />
          Add Row
        </button>
      )}
    </div>
  );

  return (
    <div 
      {...attributes}
      contentEditable={false}
      className={`data-table-element relative my-4 rounded-lg border-2 transition-colors ${
        selected && focused ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-gray-300'
      }`}
    >
      {children}

      {/* Controls - visible when selected or on hover */}
      <div 
        className={`absolute top-2 right-2 flex items-center gap-2 z-10 transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0 hover:opacity-100'
        }`}
        contentEditable={false}
      >
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowSettings(!showSettings);
          }}
          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
          title="Settings"
        >
          <Settings size={16} />
        </button>
        {onDelete && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(element.id);
            }}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-red-100 cursor-pointer"
            title="Delete"
          >
            <X size={16} className="text-red-500" />
          </button>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div 
          className="absolute top-12 right-2 bg-white rounded-lg shadow-xl border p-4 z-20 min-w-[280px]"
          contentEditable={false}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <h4 className="font-semibold mb-3">Table Settings</h4>
          
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Table Mode</label>
            <select
              value={mode}
              onChange={(e) => handleModeChange(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              {Object.entries(TABLE_MODE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={element.title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Table title"
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowSettings(false);
            }}
            className="w-full mt-2 px-3 py-2 bg-gray-100 rounded-md text-sm hover:bg-gray-200 cursor-pointer"
          >
            Close
          </button>
        </div>
      )}

      {/* Table Title */}
      {element.title && (
        <h3 className="text-lg font-semibold text-gray-800 mb-2 px-4 pt-4">
          {element.title}
        </h3>
      )}

      {/* Table Content */}
      <div className="p-4">
        {mode === TABLE_MODES.CUSTOM ? renderCustomTable() : renderDataTable()}
      </div>
    </div>
  );
}

export default DataTableElement;

